import { useParams } from "react-router-dom";
import {
  StepType,
  useFederationOrderDetailQuery,
  useAcceptFederatedWorkMutation,
  useDeclineFederatedWorkMutation,
  useCompleteFederatedWorkMutation,
} from "../__generated__/graphql";
import { OriginTenantBadge } from "../components/OriginTenantBadge";
import { FederationMessageThread } from "../components/FederationMessageThread";
import { usePrompt } from "@clearpath/design-system";
import { vendorFailureCopy, vendorLoadFailureSentence } from "../lib/vendor-failure-copy";

const LOAD_ORDER_FAILURE_SENTENCE = vendorLoadFailureSentence("order");

/**
 * Simplified order-detail page for federated work.
 *
 * Salvageable from apps/main/OrderDetailPage via @clearpath/ui-components
 * once the component is promoted there (currently the package is an empty
 * scaffold — see packages/ui-components/src/index.ts). Until then we render
 * a thinner vendor-flavored detail inline and rely on resolver-level
 * authorization to enforce event_visibility_config: fields not on the
 * whitelist come back null, and we render "—".
 */
export function FederationOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const orderId = id ?? "";

  const { data, loading, error, refetch } = useFederationOrderDetailQuery({
    variables: { id: orderId },
    fetchPolicy: "cache-and-network",
    skip: !id,
  });

  const [accept, { loading: accepting }] = useAcceptFederatedWorkMutation();
  const [decline, { loading: declining }] = useDeclineFederatedWorkMutation();
  const [complete, { loading: completing }] = useCompleteFederatedWorkMutation();
  const prompt = usePrompt();

  if (!id) return <p>Missing order id.</p>;
  if (loading && !data) return <p>Loading order…</p>;
  if (error) {
    return (
      <p role="alert">
        {vendorFailureCopy(error, { generic: LOAD_ORDER_FAILURE_SENTENCE })}
      </p>
    );
  }

  const order = data?.order;
  if (!order) return <p>Order not found or outside your federation scope.</p>;

  // The first EXTERNAL_VENDOR step run is the one this vendor tenant acts on.
  const activeStep =
    order.stepRuns?.edges.find(
      ({ node }) => node.stepDefinition?.stepType === StepType.ExternalVendor,
    )?.node ?? null;
  const federationLinkId = activeStep?.vendorFederationLinkId ?? null;

  return (
    <article className="federation-order-detail">
      <header>
        <h1>Order {order.barcodeValue ?? order.id}</h1>
        {activeStep?.vendorFederationLink && (
          <OriginTenantBadge
            partnerTenantId={activeStep.vendorFederationLink.partnerTenantId}
            label={activeStep.vendorFederationLink.label}
          />
        )}
        <dl>
          <dt>Type</dt>
          <dd>{order.orderType}</dd>
          <dt>Status</dt>
          <dd>{order.status}</dd>
          <dt>SLA</dt>
          <dd>{order.estimatedCompletionAt ?? "—"}</dd>
        </dl>
      </header>

      <section aria-label="Products">
        <h2>Products</h2>
        <ul>
          {order.orderProducts?.map((p) => (
            <li key={p.id}>
              {p.productType?.name ?? p.productTypeId} · qty {p.quantity}
            </li>
          ))}
        </ul>
      </section>

      <section aria-label="Files shared with this work">
        <h2>Files</h2>
        <ul>
          {order.files.edges.map(({ node }) => (
            <li key={node.id}>
              {node.filename} <small>({node.mimeType})</small>
            </li>
          ))}
        </ul>
        <p>
          <small>
            Files are served via presigned URLs minted by the origin tenant's
            federation_file_service. They expire; refresh the page if a link
            stops working.
          </small>
        </p>
      </section>

      {activeStep && (
        <section aria-label="Actions">
          <h2>Actions</h2>
          <div className="actions">
            <button
              type="button"
              disabled={accepting || !isStatus(activeStep.status, "pending", "ready")}
              onClick={async () => {
                await accept({ variables: { workItemId: activeStep.id } });
                void refetch();
              }}
            >
              Accept
            </button>
            <button
              type="button"
              disabled={declining}
              onClick={async () => {
                const reason = await prompt({
                  title: "Decline federated work",
                  description: "The origin tenant will be notified with this reason.",
                  placeholder: "e.g. capacity full, wrong capability",
                  submitLabel: "Decline",
                });
                if (!reason) return;
                await decline({
                  variables: { workItemId: activeStep.id, reason },
                });
                void refetch();
              }}
            >
              Decline
            </button>
            <button
              type="button"
              disabled={completing || !isStatus(activeStep.status, "in_progress")}
              onClick={async () => {
                const notes = await prompt({
                  title: "Completion notes",
                  description: "Optional — appears on the origin tenant's step timeline.",
                  placeholder: "e.g. shipped with UPS Ground, tracking attached",
                  submitLabel: "Complete",
                  allowEmpty: true,
                });
                if (notes === null) return;
                await complete({
                  variables: {
                    workItemId: activeStep.id,
                    outputFileIds: [],
                    notes: notes || undefined,
                  },
                });
                void refetch();
              }}
            >
              Complete
            </button>
          </div>
        </section>
      )}

      {federationLinkId && (
        <FederationMessageThread
          federationLinkId={federationLinkId}
          stepRunId={activeStep?.id}
        />
      )}
    </article>
  );
}

/** Match DB-native lowercase status ("pending"/"ready"/"in_progress") AND the
 *  codegen enum (Pending/Ready/InProgress). StepRunStatus enum values vary
 *  in case across SDL; normalising to lowercase covers both. */
function isStatus(status: unknown, ...variants: string[]): boolean {
  const s = String(status).toLowerCase();
  return variants.some((v) => v === s);
}
